/**
 * Centralized debug-logger.
 *
 * Pattern: `const dlog = createDebugLogger('tag')` — dann `dlog('label', payload)`.
 *
 * Vorher: pro Stelle ein `console.debug('[orbit:tag] label', payload)` mit
 * `// eslint-disable-next-line no-console` Vorspann. Verstreuter Mist.
 *
 * Output:
 *   [orbit:tag] label {payload}
 *
 * In Production (`import.meta.env.PROD`) ist die Funktion ein Noop — der
 * Bundler tree-shaked die Aufrufe komplett raus.
 *
 * Optional aktivierbar in Production via:
 *   localStorage.setItem('ORBIT_DEBUG', '1')
 * Dann landen die Logs auch im prod-Build, falls jemand debugen muss.
 */

const ENABLED_IN_PROD = (() => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage?.getItem('ORBIT_DEBUG') === '1';
  } catch {
    return false;
  }
})();

const ACTIVE = import.meta.env?.DEV || ENABLED_IN_PROD;

const NOOP = () => {};

export function createDebugLogger(tag) {
  if (!ACTIVE) return NOOP;
  const prefix = `[orbit:${tag}]`;
  return (...args) => {
    // eslint-disable-next-line no-console
    console.debug(prefix, ...args);
  };
}

/**
 * Error-Logger — analog zu createDebugLogger aber für Errors.
 *
 * In Production: nur error.code (oder message-first-line, falls kein Code).
 * Vermeidet dass `details`-Payloads mit Server-Internals in Screenshot-
 * basierten Helpdesk-Tickets landen.
 *
 * In Dev (oder mit ORBIT_DEBUG=1 aktiv): volle Payload.
 *
 * Per security-review LOW-2.
 */
export function createErrorLogger(tag) {
  const prefix = `[orbit:${tag}]`;
  return (label, err) => {
    if (ACTIVE) {
      // eslint-disable-next-line no-console
      console.error(prefix, label, err);
      return;
    }
    const code = err?.code ?? err?.data?.error?.code ?? null;
    const messageHead = typeof err?.message === 'string'
      ? err.message.split('\n')[0].slice(0, 120)
      : null;
    // eslint-disable-next-line no-console
    console.error(prefix, label, code ? { code } : { error: messageHead });
  };
}
